import type { MachineId, Material, Tag, TokenSpec, TokenState } from '../shared/contract.js';
import type { Vec3 } from './vec.js';

/**
 * A token as the sim knows it: the contract's TokenSpec plus the runtime bookkeeping
 * that the tag defaults need. Nothing here is machine-specific — that is the whole
 * point of the grammar: one token type, every cabinet.
 */
export interface TokenRuntime {
  id: number;
  material: Material;
  faceValue: number;
  /** base face value at deployment; [Zins] doubles faceValue away from this */
  baseValue: number;
  radius: number;
  tags: Tag[];
  machine: MachineId;

  /** Rapier rigid-body handle, or -1 for tokens that are currently abstract
   *  (e.g. a symbol windowed onto a BANDIT reel drum). */
  body: number;
  /** resolved mass, cached at spawn — querying Rapier for it every tick × 300 tokens
   *  was measurable, and mass cannot change mid-flight anyway. */
  mass: number;

  /** seconds alive in the current machine (contract: drives Zins visuals) */
  age: number;
  /** seconds since the last [Zins] doubling; a [Spalt] split resets it */
  zinsClock: number;
  /** [Gummi]: first 3 bounces are lossless */
  bounces: number;
  /** [Geist]: armed -> phasing (inside the first collider) -> spent */
  ghost: 'armed' | 'phasing' | 'spent';
  /** the body id we are currently phasing through (-1 = field geometry) */
  /** [Kleber]: welded into the field, now terrain */
  stuck: boolean;
  /**
   * Sleep management, not a game rule: a buried, sleeping coin is turned into a fixed
   * body so it stops propagating wake-ups through the pile's contact island. It is
   * revived the moment it is uncovered, impulsed, or approached. See Muenzschieber.
   */
  sedimented: boolean;
  /** enchant potency; halves on each [Spalt] split (children inherit at half) */
  potency: number;

  /** last scoring event, for [Echo] */
  lastScore: { pocket: string; value: number; at: Vec3 } | null;
  /** seconds with (near) zero velocity — drives onTokenIdle */
  idleSeconds: number;
  /** asleep AND already boundary-checked once, so machines can skip it entirely.
   *  Cleared automatically the moment Rapier wakes the body. */
  dormant: boolean;
  /** deployed = in play in a machine (as opposed to sitting in Der Beutel) */
  live: boolean;
  /** consumed (scored / lost / merged away) */
  alive: boolean;
  /** generation counter for split lineage, used in receipts */
  generation: number;
}

export function hasTag(t: { tags: readonly Tag[] }, tag: Tag): boolean {
  return t.tags.indexOf(tag) >= 0;
}

export const MATERIAL_MULT: Record<Material, number> = {
  kupfer: 1,
  silber: 2,
  gold: 5,
  obsidian: 8,
};

export function makeRuntime(spec: TokenSpec, machine: MachineId): TokenRuntime {
  return {
    id: spec.id,
    material: spec.material,
    faceValue: spec.faceValue,
    baseValue: spec.faceValue,
    radius: spec.radius,
    tags: [...spec.tags],
    machine,
    body: -1,
    mass: 1,
    age: 0,
    zinsClock: 0,
    bounces: 0,
    ghost: hasTag(spec, 'Geist') ? 'armed' : 'spent',
    stuck: false,
    sedimented: false,
    potency: 1,
    lastScore: null,
    idleSeconds: 0,
    dormant: false,
    live: false,
    alive: true,
    generation: 0,
  };
}

export function toSpec(t: TokenRuntime): TokenSpec {
  return {
    id: t.id,
    material: t.material,
    faceValue: t.faceValue,
    radius: t.radius,
    tags: [...t.tags],
  };
}

export function toState(
  t: TokenRuntime,
  pos: Vec3,
  quat: [number, number, number, number],
  vel: Vec3,
  asleep: boolean,
): TokenState {
  return {
    id: t.id,
    material: t.material,
    faceValue: t.faceValue,
    radius: t.radius,
    tags: [...t.tags],
    pos: [pos.x, pos.y, pos.z],
    quat,
    vel: [vel.x, vel.y, vel.z],
    asleep,
    age: t.age,
    // contract v1: [Kleber] terrain is not the same thing as an ordinary sleeping coin,
    // and the renderer cannot tell them apart from `asleep` alone (~226 pile coins sleep)
    stuck: t.stuck,
  };
}
